import React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Link from "arena/components/Link";
import ReactGA from "react-ga4";
import { useArena } from "core/components/ArenaProvider";
import { getLogoSrcSet } from "core/utils/getSrcSet";

/** Mobile: Figma Frame 3 (288:546) — 56×24; desktop pin image up to 160×44 */
const PIN_IMAGE_MAX = {
  height: { xs: 24, md: 44 },
  width: { xs: 56, md: 160 },
};

function getLinkProps(url, arenaUrl) {
  if (!url) {
    return { isExternal: false, to: "/" };
  }

  if (!url.startsWith("http")) {
    return { isExternal: false, to: url };
  }

  try {
    const linkUrl = new URL(url);
    const baseUrl = new URL(arenaUrl);

    if (linkUrl.origin === baseUrl.origin) {
      return {
        isExternal: false,
        to: `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`,
      };
    }

    return { isExternal: true, href: url };
  } catch {
    return { isExternal: false, to: url };
  }
}

function trackPinClick(item) {
  ReactGA.event({
    category: "Header",
    action: "Click to menu pin item",
    label: item.type === "BUTTON" ? item.label : item.id,
  });
}

function MenuPinItemLink({ item, children, sx }) {
  const arena = useArena();
  const { isExternal, to, href } = getLinkProps(item.url, arena.url);

  const onClick = () => {
    trackPinClick(item);
  };

  const linkSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    textDecoration: "none",
    color: "inherit",
    ...sx,
  };

  if (isExternal) {
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        sx={linkSx}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box component={Link} to={to} onClick={onClick} sx={linkSx}>
      {children}
    </Box>
  );
}

MenuPinItemLink.propTypes = {
  item: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

const MAX_PIN_ITEMS = 2;

export default function HeaderMenuPins({ menuPinItems }) {
  const arena = useArena();
  const theme = useTheme();
  const isCompactHeader = useMediaQuery(theme.breakpoints.down("md"));

  if (!menuPinItems?.length) {
    return null;
  }

  const items = menuPinItems.slice(0, MAX_PIN_ITEMS);
  const buttonSize = isCompactHeader ? "small" : "medium";

  return (
    <Stack direction="row" alignItems="center" sx={{ gap: { xs: 0.5, md: 3 } }}>
      {items.map((item) => {
        if (item.type === "IMAGE" || item.type === "image") {
          if (!item.logo?.url) {
            return null;
          }

          return (
            <MenuPinItemLink key={item.id} item={item}>
              <Box
                component="img"
                src={item.logo.url}
                srcSet={
                  item.logo.thumbnails
                    ? getLogoSrcSet(item.logo.thumbnails)
                    : undefined
                }
                alt=""
                sx={{
                  display: "block",
                  width: "auto",
                  height: "auto",
                  maxWidth: PIN_IMAGE_MAX.width,
                  maxHeight: PIN_IMAGE_MAX.height,
                  objectFit: "contain",
                }}
              />
            </MenuPinItemLink>
          );
        }

        if ((item.type === "BUTTON" || item.type === "button") && item.label) {
          const linkProps = getLinkProps(item.url, arena.url);
          const buttonProps = {
            disableElevation: true,
            variant: "outlined",
            color: "primary",
            size: buttonSize,
            sx: {
              whiteSpace: "nowrap",
            },
          };

          if (linkProps.isExternal) {
            return (
              <Button
                key={item.id}
                {...buttonProps}
                component="a"
                href={linkProps.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackPinClick(item)}
              >
                {item.label}
              </Button>
            );
          }

          return (
            <Button
              key={item.id}
              {...buttonProps}
              component={Link}
              to={linkProps.to}
              onClick={() => trackPinClick(item)}
            >
              {item.label}
            </Button>
          );
        }

        return null;
      })}
    </Stack>
  );
}

HeaderMenuPins.propTypes = {
  menuPinItems: PropTypes.array,
};
